/*
 * What the browser remembers for this app, it remembers for one user:
 * every key carries the subject uuid of the login it belongs to, so
 * two people sharing a browser do not inherit each other's bookmarks,
 * table heights, dashboard order and settings.
 *
 * Two things cannot be filed under a user and stay where they are: the
 * session itself (`jwt_token`, `simulatedAuthSession`) — it is what
 * says who the user is — and the chosen language (`i18nextLng`), which
 * the login page already speaks, before there is a user at all.
 *
 * Where nobody is logged in there is nobody to remember anything for:
 * reads answer with null and writes are dropped, rather than falling
 * back onto a shelf that everyone shares. The subject is set while the
 * tree below the login renders (see `UserStorageScope` in main.tsx),
 * so it stands before anything reads.
 *
 * Every call is guarded: a browser in private mode, or one with
 * storage switched off, answers nothing and swallows what it is told —
 * the app then remembers nothing, which is a poorer app but a working
 * one.
 */
const SCOPE_PREFIX = 'user:';

let subjectUuid: string | null = null;

/* The user everything is filed under from now on — the subject uuid of
   the login (`sub` in the token). Null while nobody is logged in. */
export function setStorageSubject(uuid: string | null | undefined): void {
  subjectUuid = uuid || null;
}

/* Who that is at the moment — what a module caching something it read
   has to watch, or it would hand the next user the last one's list
   (see bookmarks and recentRecords). */
export function storageSubject(): string | null {
  return subjectUuid;
}

/* The stored key an app key stands for, or null while there is no user
   to file it under. */
export function userKey(key: string): string | null {
  return subjectUuid ? `${SCOPE_PREFIX}${subjectUuid}:${key}` : null;
}

const scopedStore = (backing: () => Storage) => ({
  getItem(key: string): string | null {
    const stored = userKey(key);
    if (!stored) return null;
    try {
      return backing().getItem(stored);
    } catch {
      return null;
    }
  },

  setItem(key: string, value: string): void {
    const stored = userKey(key);
    if (!stored) return;
    try {
      backing().setItem(stored, value);
    } catch {
      /* nothing to remember it with — the app forgets */
    }
  },

  removeItem(key: string): void {
    const stored = userKey(key);
    if (!stored) return;
    try {
      backing().removeItem(stored);
    } catch {
      /* it is gone either way as far as this app is concerned */
    }
  },

  /* the app keys this user has something stored under, as the app
     spells them — the scope is the store's business, not the
     caller's (see the reset in UiSettingsContext) */
  keys(): string[] {
    const mine = userKey('');
    if (!mine) return [];
    try {
      const store = backing();
      return [...Array(store.length).keys()]
        .map((index) => store.key(index))
        .filter((key): key is string => !!key?.startsWith(mine))
        .map((key) => key.slice(mine.length));
    } catch {
      return [];
    }
  },
});

export const userStore = scopedStore(() => window.localStorage);
export const userSessionStore = scopedStore(() => window.sessionStorage);

/* What the app wrote before it filed anything under a user: nobody
   reads it any more, and left lying about it would be a second, older
   set of everyone's settings. Named one by one rather than swept by
   pattern — the origin is not this app's alone to clear. */
const UNSCOPED_KEYS = [
  'bookmarks',
  'recentRecords',
  'seenBuild',
  'overviewCardOrder',
  /* an even older shape of the settings, one key holding them all —
     still lying in browsers that saw it */
  'uiSettings',
  'uiSettings.copyUuid',
  'uiSettings.showExampleData',
  'uiSettings.showHosting',
  'uiSettings.wideLayout',
  'uiSettings.showVersions',
  'uiSettings.dateFormat',
];
const UNSCOPED_PREFIXES = ['crudSplit:', 'crudRows:', 'crudFilterMode:'];

export function forgetUnscopedStorage(): void {
  try {
    const store = window.localStorage;
    const stale = [...Array(store.length).keys()]
      .map((index) => store.key(index))
      .filter(
        (key): key is string =>
          !!key &&
          (UNSCOPED_KEYS.includes(key) ||
            UNSCOPED_PREFIXES.some((prefix) => key.startsWith(prefix)))
      );
    stale.forEach((key) => store.removeItem(key));
    window.sessionStorage.removeItem('selectedPartnerUuid');
  } catch {
    /* no storage to clear out */
  }
}
